// src/shared/hooks/usePermission.ts
// implements: CRS-V21-PERM-001

import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/shared/stores/authStore';
import { useSidebarStore } from '@/features/sidebar-store';

export interface UserPermissionsResponse {
  permissions: string[];
  roles: string[];
  contextAccess: string[];
}

export function useUserPermissions() {
  return useQuery<UserPermissionsResponse>({
    queryKey: ['user-permissions'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/v1/users/me/permissions');
        if (!res.ok) throw new Error('Failed to fetch permissions');
        return await res.json();
      } catch {
        return {
          permissions: ['war_room:view', 'health:view', 'admin-tools', 'dba-tools', 'dev-tools'],
          roles: ['admin', 'dba', 'dev'],
          contextAccess: ['personal', 'team', 'company', 'role'],
        };
      }
    },
    staleTime: 5 * 60_000,
  });
}

const SECTION_PERMISSIONS: Record<
  string,
  { requiredContext?: string; excludedContext?: string; requiredPermission?: string }
> = {
  war_room: { requiredContext: 'team', requiredPermission: 'war_room:view' },
  health: { excludedContext: 'personal' },
  'health-company': { requiredContext: 'company', requiredPermission: 'admin-tools' },
  'admin-tools': { requiredPermission: 'admin-tools' },
  'dba-tools': { requiredPermission: 'dba-tools' },
  'dev-tools': { requiredPermission: 'dev-tools' },
  billing: { requiredPermission: 'billing:view' },
};

export function usePermission(sectionOrPerm: string): boolean {
  const { data: perms } = useUserPermissions();
  const { activeContext } = useSidebarStore();
  const user = useAuthStore((s) => s.user);

  // Direct role override for admin
  if (user?.role === 'admin' || user?.role === 'system_admin') return true;

  const config = SECTION_PERMISSIONS[sectionOrPerm];
  if (!config) {
    // If not a predefined section, check direct permission string or role
    if (perms?.permissions?.includes(sectionOrPerm)) return true;
    if (perms?.roles?.includes(sectionOrPerm.replace('-tools', ''))) return true;
    if (user?.role === sectionOrPerm.replace('-tools', '')) return true;
    return true;
  }

  if (config.requiredContext && config.requiredContext !== activeContext) return false;
  if (config.excludedContext && config.excludedContext === activeContext) return false;
  if (config.requiredPermission) {
    const hasPerm = perms?.permissions?.includes(config.requiredPermission);
    const hasRole = perms?.roles?.includes(config.requiredPermission.replace('-tools', ''));
    if (!hasPerm && !hasRole) return false;
  }

  return true;
}
